/**
 * Hook for fetching shipment events and details.
 */

import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/utils/apiClient";
import type {
  ShipmentEventApi,
  ShipmentEventsApiRequest,
  ShipmentEventsApiResponse,
  GetShipmentByIdResponse,
} from "@services/shipments/types";

interface UseShipmentDataResult {
  events: ShipmentEventApi[];
  shipment: GetShipmentByIdResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches shipment events and details.
 * @param shipmentId - The shipment ID to fetch data for
 * @param includeInternalReminders - Whether to include internal reminders
 * @returns Object containing events, shipment, loading state, error, and refetch function
 */
export function useShipmentData(
  shipmentId: string,
  includeInternalReminders: boolean
): UseShipmentDataResult {
  const [events, setEvents] = useState<ShipmentEventApi[]>([]);
  const [shipment, setShipment] = useState<GetShipmentByIdResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch both shipment events and shipment details
      const [eventsResponse, shipmentResponse] = await Promise.all([
        apiClient.post<ShipmentEventsApiResponse>("/shipments/events", {
          shipmentId,
        } as ShipmentEventsApiRequest),
        apiClient.post<GetShipmentByIdResponse>("/shipments/get-by-id", {
          shipmentId,
          includeInternalReminders,
        }),
      ]);

      setEvents(eventsResponse.events);
      setShipment(shipmentResponse);
    } catch (err) {
      console.error("Error fetching shipment data:", err);
      setError("No shipment available for this email");
    } finally {
      setLoading(false);
    }
  }, [shipmentId, includeInternalReminders]);

  useEffect(() => {
    if (shipmentId) {
      fetchEvents();
    }
  }, [shipmentId, fetchEvents]);

  return {
    events,
    shipment,
    loading,
    error,
    refetch: fetchEvents,
  };
}
